import * as bip39 from '@scure/bip39'
import { wordlist } from '@scure/bip39/wordlists/english'
import { HDKey } from '@scure/bip32'
import * as secp256k1 from '@noble/secp256k1'
import { sha256 } from '@noble/hashes/sha256'
import { ripemd160 } from '@noble/hashes/ripemd160'
import bs58check from 'bs58check'

// Dogecoin BIP44 derivation path: m/44'/3'/0'/0/0
const DOGE_DERIVATION_PATH = "m/44'/3'/0'/0/0"

// Dogecoin mainnet version bytes
const DOGE_PUBKEY_HASH = 0x1e // P2PKH address prefix (D)
const DOGE_WIF_PREFIX = 0x9e // WIF prefix for mainnet

// Dogecoin message prefix for BIP-137 signing
// \x19 = 25, which is the length of "Dogecoin Signed Message:\n"
const DOGE_MESSAGE_PREFIX = '\x19Dogecoin Signed Message:\n'

/**
 * Validate a BIP39 mnemonic phrase
 */
export function validateMnemonic(mnemonic: string): boolean {
  return bip39.validateMnemonic(mnemonic.toLowerCase().trim(), wordlist)
}

/**
 * Get the BIP39 wordlist for validation/autocomplete
 */
export function getWordlist(): string[] {
  return [...wordlist]
}

/**
 * Derive private key from mnemonic seed phrase
 * Uses Dogecoin BIP44 path: m/44'/3'/0'/0/0
 */
export function mnemonicToPrivateKey(mnemonic: string): Uint8Array {
  const normalizedMnemonic = mnemonic.toLowerCase().trim()

  if (!validateMnemonic(normalizedMnemonic)) {
    throw new Error('Invalid mnemonic phrase')
  }

  // Convert mnemonic to seed
  const seed = bip39.mnemonicToSeedSync(normalizedMnemonic)

  // Derive HD key from seed
  const hdKey = HDKey.fromMasterSeed(seed)

  // Derive child key using BIP44 path for Dogecoin
  const childKey = hdKey.derive(DOGE_DERIVATION_PATH)

  if (!childKey.privateKey) {
    throw new Error('Failed to derive private key')
  }

  return childKey.privateKey
}

/**
 * Get Dogecoin address from private key (P2PKH)
 */
export function getAddressFromPrivateKey(privateKey: Uint8Array): string {
  // Get public key (compressed)
  const publicKey = secp256k1.getPublicKey(privateKey, true)

  // Hash160: SHA256 then RIPEMD160
  const sha256Hash = sha256(publicKey)
  const hash160 = ripemd160(sha256Hash)

  // Add version byte and encode with base58check
  const versionedHash = new Uint8Array(21)
  versionedHash[0] = DOGE_PUBKEY_HASH
  versionedHash.set(hash160, 1)

  return bs58check.encode(versionedHash)
}

/**
 * Export private key to WIF format
 */
export function privateKeyToWIF(privateKey: Uint8Array): string {
  // Add version byte and compression flag
  const extended = new Uint8Array(34)
  extended[0] = DOGE_WIF_PREFIX
  extended.set(privateKey, 1)
  extended[33] = 0x01 // Compressed flag

  return bs58check.encode(extended)
}

/**
 * Import private key from WIF format
 */
export function wifToPrivateKey(wif: string): Uint8Array {
  const decoded = bs58check.decode(wif)

  // Check version byte
  if (decoded[0] !== DOGE_WIF_PREFIX) {
    throw new Error('Invalid WIF version byte')
  }

  // Extract private key (skip version byte, handle compression flag)
  if (decoded.length === 34 && decoded[33] === 0x01) {
    // Compressed
    return decoded.slice(1, 33)
  } else if (decoded.length === 33) {
    // Uncompressed
    return decoded.slice(1, 33)
  } else {
    throw new Error('Invalid WIF length')
  }
}

/**
 * Format message for BIP-137 signing
 * Format: prefix + varint(message_length) + message
 * The prefix already includes its length byte (\x19 = 25)
 */
function formatSignedMessage(message: string): Uint8Array {
  const messageBytes = new TextEncoder().encode(message)
  const prefixBytes = new TextEncoder().encode(DOGE_MESSAGE_PREFIX)
  const messageLen = encodeVarint(messageBytes.length)

  // Concatenate: prefix + message_len + message
  const result = new Uint8Array(
    prefixBytes.length + messageLen.length + messageBytes.length
  )

  let offset = 0
  result.set(prefixBytes, offset)
  offset += prefixBytes.length
  result.set(messageLen, offset)
  offset += messageLen.length
  result.set(messageBytes, offset)

  return result
}

/**
 * Encode integer as Bitcoin varint
 */
function encodeVarint(n: number): Uint8Array {
  if (n < 0xfd) {
    return new Uint8Array([n])
  } else if (n <= 0xffff) {
    return new Uint8Array([0xfd, n & 0xff, (n >> 8) & 0xff])
  } else if (n <= 0xffffffff) {
    return new Uint8Array([
      0xfe,
      n & 0xff,
      (n >> 8) & 0xff,
      (n >> 16) & 0xff,
      (n >> 24) & 0xff,
    ])
  } else {
    throw new Error('Varint too large')
  }
}

/**
 * Sign a message using BIP-137 format (recoverable signature)
 * Returns base64 encoded signature
 */
export async function signMessage(
  privateKey: Uint8Array,
  message: string
): Promise<string> {
  // Format and double SHA256 the message
  const formattedMessage = formatSignedMessage(message)
  const messageHash = sha256(sha256(formattedMessage))

  // Get the expected public key (compressed)
  const publicKey = secp256k1.getPublicKey(privateKey, true)

  // Sign the message
  const signature = await secp256k1.signAsync(messageHash, privateKey, {
    lowS: true,
  })

  // Determine recovery ID by trying both values (0 and 1)
  let recoveryId = 0
  for (let i = 0; i < 2; i++) {
    try {
      const sigWithRecovery = signature.addRecoveryBit(i)
      const recoveredPoint = sigWithRecovery.recoverPublicKey(messageHash)
      if (recoveredPoint && bytesEqual(recoveredPoint.toRawBytes(true), publicKey)) {
        recoveryId = i
        break
      }
    } catch {
      // Try next recovery ID
    }
  }

  // Build BIP-137 format signature
  // For P2PKH compressed: header = 27 + recovery_id + 4
  const headerByte = 27 + recoveryId + 4

  // Signature is: header (1 byte) + r (32 bytes) + s (32 bytes)
  const sigBytes = new Uint8Array(65)
  sigBytes[0] = headerByte
  sigBytes.set(signature.toCompactRawBytes(), 1)

  // Return base64 encoded
  return btoa(String.fromCharCode(...sigBytes))
}

/**
 * Compare two byte arrays for equality
 */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Generate authentication headers for API requests
 */
export async function generateAuthHeaders(
  privateKey: Uint8Array,
  address: string,
  requestPath: string
): Promise<Record<string, string>> {
  const timestamp = Math.floor(Date.now() / 1000).toString()
  const message = `${address}:${timestamp}:${requestPath}`

  const signature = await signMessage(privateKey, message)

  return {
    'Content-Type': 'application/json',
    'X-Wallet-Address': address,
    'X-Signature': signature,
    'X-Timestamp': timestamp,
  }
}
